import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  MenuItem,
  Modal,
  Paper,
  Popper,
  styled,
  Theme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { CommonColors } from "../colors/commonColors";
import clsx from "clsx";
import { onRtl } from "../rtl/rtlStyles";
//region [[ Styles ]]

const PatientFacingDropDownView = styled("div")({
  height: "fit-content",
  position: "relative",
});

const OptionsWrapper = styled(Paper)(
  ({ theme, width }: { theme?: Theme; width?: number }) => ({
    minWidth: "130px",
    width: width || "100%",
    ...onRtl({
      direction: "rtl",
    }),
  }),
);

const Selector = styled(Button)({
  ".patient-facing-drop-down &, .patient-facing-drop-down &:hover, .patient-facing-drop-down &:focus": {
    boxShadow: "none",
    borderRadius: "4px",
    border: `1px solid ${CommonColors.DefaultDark}`,
    padding: "8px 16px",
    minWidth: "130px",
    justifyContent: "space-between",
    // Placeholder
    textTransform: "none",
    fontSize: "16px",
    color: "#abb0b5",
    "&.active:not(:disabled)": {
      border: `1px solid ${CommonColors.DefaultDark}`,
      color: CommonColors.Default,
    },
    width: "100%",
    background: CommonColors.White,
  },
  "&:not(.placeholder) > li": {
    textTransform: "capitalize",
    fontSize: "16px",
  },
  "&": {},
});

const Option = styled(MenuItem)({
  textTransform: "capitalize",
});

//endregion [[ Styles ]]

//region [[ Props ]]

export type PatientFacingDropDownOption<T> = {
  name: string;
  value: T;
};

export interface PatientFacingDropDownProps<T> {
  className?: string;
  placeholder?: string;
  value?: T;
  onValueChanged?: (e, value) => void;
  options: PatientFacingDropDownOption<T>[];
  disabled?: boolean;
  isEmbedded?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientFacingDropDown = <T,>({
  options,
  onValueChanged,
  isEmbedded,
  ...props
}: PatientFacingDropDownProps<T>) => {
  const menuRef = useRef<any>(null);
  const menuAnchorRef = useRef<any>(null);
  const [menuIsShown, setMenuIsShown] = useState(false);
  const [currentValue, setCurrentValue] = useState<
    PatientFacingDropDownOption<T>
  >(null);
  const [menuWidth, setMenuWidth] = useState(100);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuAnchorRef.current) {
        setMenuWidth(menuAnchorRef.current.getBoundingClientRect?.().width);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuIsShown(false);
      } else {
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const createOptionSelector = useCallback(
    (i) => (e) => {
      setCurrentValue(options[i]);
      onValueChanged?.(e, options[i].value);
      setMenuIsShown(false);
    },
    [onValueChanged, options],
  );

  useEffect(() => {
    if (props.value) {
      const currentIndex = options.findIndex(
        (o) =>
          o.value === props.value ||
          (typeof props.value === "string" && o.name === props.value),
      );
      if (currentIndex > -1) {
        setCurrentValue(options[currentIndex]);
      }
    } else {
      setCurrentValue(null);
    }
  }, [options, props.value]);

  const handleMenuClicked = () => {
    if (props.disabled) return;
    setMenuIsShown(true);

    setTimeout(() => {
      if (menuRef.current && !isEmbedded) {
        console.log("Scrolling to", menuRef.current);
        // Make sure they have the same width
        menuRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "end",
        });
      }
    }, 300);
  };

  return (
    <PatientFacingDropDownView
      className={clsx(props.className, "patient-facing-drop-down")}
    >
      <Selector
        className={clsx(
          currentValue ? "active" : "placeholder",
          props.disabled ? "disabled" : null,
        )}
        onClick={handleMenuClicked}
        ref={menuAnchorRef}
        endIcon={<ArrowDropDownIcon />}
      >
        {currentValue?.name || props.placeholder}
      </Selector>
      <Popper
        open={menuIsShown}
        anchorEl={menuAnchorRef.current}
        placement={"bottom"}
        style={{ zIndex: 9999 }}
      >
        <OptionsWrapper ref={menuRef} width={menuWidth}>
          {options.map((option, i) => (
            <Option key={i} onClick={createOptionSelector(i)}>
              {option.name}
            </Option>
          ))}
        </OptionsWrapper>
      </Popper>
    </PatientFacingDropDownView>
  );
};
